import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

export default function Drip({ embedded }) {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [autoOn, setAutoOn] = useState(false);
  const load = () => client.get("/admin/drip").then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => { load(); client.get("/admin/settings").then((r) => setAutoOn(!!r.data.auto_drip_enabled)).catch(() => {}); }, []);

  const toggleAuto = async () => {
    const next = !autoOn;
    try { const r = await client.patch("/admin/settings", { auto_drip_enabled: next }); setAutoOn(!!r.data.auto_drip_enabled); toast.success(next ? "Auto-generation ON — the engine will queue drops automatically." : "Auto-generation OFF — nothing is generated unless you click Run now."); }
    catch { toast.error("Could not update setting."); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await client.post("/admin/drip/run?force=true");
      toast.success(`Queued ${r.data.generated} monthly drop(s) as drafts.`);
      await load();
    } catch { toast.error("Drip run failed."); }
    setBusy(false);
  };

  return (
    <div>
      {!embedded && <Header kicker="Retention" title="Monthly Drip Engine" />}
      <div className={embedded ? "py-8" : "p-6 sm:p-10"}>
        {/* Auto-generation master switch */}
        <div className={`flex items-center justify-between gap-4 border p-5 sm:p-6 mb-6 ${autoOn ? "border-gloss-gold/50 bg-gloss-gold/10" : "border-ink/20 bg-secondary"}`} data-testid="auto-drip-toggle-panel">
          <div className="min-w-0">
            <p className="font-grotesk text-lg text-ink flex items-center gap-2"><Zap size={16} /> Automatic content generation</p>
            <p className="font-mono text-[11px] text-ink/55 mt-1">{autoOn ? "ON — the engine auto-queues each member's next drop on a schedule." : "OFF — nothing is generated automatically. Content only appears when you create it or press “Run now”."}</p>
          </div>
          <button onClick={toggleAuto} role="switch" aria-checked={autoOn} className={`relative w-16 h-9 rounded-full shrink-0 transition-colors ${autoOn ? "bg-gloss-gold" : "bg-ink/25"}`} data-testid="auto-drip-toggle">
            <span className={`absolute top-1 left-1 w-7 h-7 rounded-full bg-cream shadow transition-transform ${autoOn ? "translate-x-7" : ""}`} />
          </button>
        </div>
        <div className="bg-ink text-cream p-6 sm:p-8 grain mb-10" data-testid="drip-panel">
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="max-w-xl">
              <p className="overline text-gloss-gold mb-2">Automated retention</p>
              <p className="font-serif text-2xl sm:text-3xl leading-tight">Each member gets a fresh monthly drop — blogs and social — auto-queued as drafts for your review.</p>
              <p className="font-sans text-sm text-cream/70 mt-3">{autoOn ? "The scheduler runs automatically." : "Auto-scheduling is OFF."} Use “Run now” to generate the next due drop for every member immediately (works regardless of the switch).</p>
            </div>
            <button onClick={runNow} disabled={busy} className="bg-cream text-ink px-7 py-4 overline flex items-center justify-center gap-2 hover:bg-gloss-gold transition-colors disabled:opacity-60 shrink-0" data-testid="drip-run">
              {busy ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} />} Run now
            </button>
          </div>
        </div>
        <div className="border border-ink/15" data-testid="drip-runs">
          {runs.length === 0 ? (
            <p className="px-5 py-14 text-center font-sans text-ink/50">No drops generated yet.</p>
          ) : runs.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-5 py-4 border-b border-ink/10 last:border-0">
              <div>
                <p className="font-serif text-lg tracking-tight">{r.ref?.title || `${r.kind} drop`}</p>
                <p className="font-mono text-xs text-ink/50 mt-1 uppercase tracking-wider">{r.business_name} · Month {r.month} · {r.kind}</p>
              </div>
              <span className="overline text-gloss-silver">{(r.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- shared ---------------- */
